<template>
  <b-sidebar id="add-new-user-sidebar" :visible="isAddNewUserSidebarActive" bg-variant="white"
    sidebar-class="sidebar-lg" shadow backdrop no-header right @hidden="resetForm"
    @change="(val) => $emit('update:is-add-new-user-sidebar-active', val)">
    <template #default="{ hide }">
      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
        <h5 class="mb-0">
          {{ (localQuestion != null && localQuestion.id != null) ||
            localQuestion.id != undefined
            ? $t('Update Question') : $t('Create New Question') }}
        </h5>

        <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
      </div>

      <!-- BODY -->
      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <!-- Form -->
        <b-form class="p-2" @submit.prevent="handleSubmit(handleQuestionSubmit)" @reset.prevent="resetForm">
          <!-- Username -->
          <validation-provider 
            #default="validationContext" 
            :name="$t('survey.question')" 
            rules="required" 
            vid="question"
            :custom-messages="{ required: $t('survey.validation.required', { _field_: $t('survey.question') }) }">
            <b-form-group :label="$t('survey.question')" label-for="SurveyCode" :state="getValidationState(validationContext)">
              <input v-model="questionText" type="hidden" style="display: none;" />
              <div :class="{'is-invalid': validationContext.errors[0], 'is-valid': validationContext.valid && validationContext.validated}">
                <quill-editor height="100" :options="snowOption" :content="localQuestion.description"
                  @change="onEditorChange($event)" @blur="onEditorBlur($event)" @focus="onEditorFocus($event)"
                  @ready="onEditorReady($event)" />
              </div>

              <div class="invalid-feedback" :class="{'d-block': validationContext.errors[0]}">
                {{ validationContext.errors[0] }}
              </div>
            </b-form-group>
          </validation-provider>

          <b-form-group :label="$t('survey.comment')" label-for="SurveyCode">
            <b-form-checkbox v-model="localQuestion.allowComment" value="true">
              {{ $t('survey.surveyquestion.comment') }}
            </b-form-checkbox>
          </b-form-group>

          <b-form-group :label="$t('survey.required.required')" label-for="SurveyCode">
            <b-form-checkbox v-model="localQuestion.required" value="true">
              {{ $t('survey.surveyquestion.required') }}
            </b-form-checkbox>
          </b-form-group>
          <!-- User Role -->

          <validation-provider 
            #default="validationContext" 
            :name="$t('survey.surveyquestion.question_tag')" 
            rules="required" 
            vid="questionTag"
            :custom-messages="{ required: $t('survey.validation.required', { _field_: $t('survey.surveyquestion.question_tag') }) }">
            <b-form-group :label="$t('survey.surveyquestion.question_tag')" label-for="user-role"
              :state="getValidationState(validationContext)">
              <input :value="questionTag ? (questionTag.title || questionTag) : null" type="hidden" style="display: none;" />
              <div :class="{'is-invalid': validationContext.errors[0], 'is-valid': validationContext.valid && validationContext.validated}">
                <question-tag-picker :initialtag="questionTag" :survey-info="surveyInfo" :key="surveyChecker"
                  @selectionChanged="QuestionTagUpdated" />
              </div>
              <div class="invalid-feedback" :class="{'d-block': validationContext.errors[0]}">
                {{ validationContext.errors[0] }}
              </div>
            </b-form-group>
          </validation-provider>

          <!-- Rating Options - Always visible -->
          <b-form-group :label="$t('survey.surveyquestion.value_text_min')" label-for="user-role">
            <b-col cols="12">
              <b-row v-for="(option, index) in ratingOptions" :key="index" class="mb-1">
                <b-col cols="3">
                  <b-form-input :value="option.value" disabled class="text-center" :placeholder="String(option.value)" trim />
                </b-col>
                <b-col>
                  <validation-provider
                    #default="validationContext"
                    :name="`${$t('survey.surveyquestion.option')} ${option.value}`"
                    rules="required"
                    :vid="`ratingOption${index}`"
                    :custom-messages="{ required: $t('survey.validation.required', { _field_: $t('survey.surveyquestion.option') + ' ' + option.value }) }">
                    <b-form-input 
                      v-model="option.text" 
                      :placeholder="getDefaultPlaceholder(option.value)" 
                      :state="getValidationState(validationContext)"
                      trim />
                    <div class="invalid-feedback" :class="{'d-block': validationContext.errors[0]}">
                      {{ validationContext.errors[0] }}
                    </div>
                  </validation-provider>
                </b-col>
                <b-col cols="auto" v-if="ratingOptions.length > 2">
                  <b-button variant="outline-danger" size="sm" @click="removeRatingOption(index)">
                    <feather-icon icon="XIcon" size="14" />
                  </b-button>
                </b-col>
              </b-row>
              
              <b-row v-if="ratingOptions.length < 10" class="mt-2">
                <b-col>
                  <b-button variant="outline-primary" size="sm" block @click="addRatingOption">
                    <feather-icon icon="PlusIcon" size="14" class="mr-25" />
                    {{ $t('survey.surveyquestion.add_option') }}
                  </b-button>
                </b-col>
              </b-row>
            </b-col>
          </b-form-group>

          <div v-if="
            localQuestion.questionTypeKey != null &&
            localQuestion.questionTypeKey === 'MULTIPLE_SELECTION'
          ">
            <div>
              <b-form ref="form" :style="{ height: trHeight }" class="repeater-form" @submit.prevent="repeateAgain">
                <b-form-group label="Available Answers" label-for="item-name">
                  <!-- Row Loop -->
                  <b-row v-for="(item, index) in localQuestion.questionAnswers" :id="item.id" :key="item.id" ref="row">
                    <b-col lg="1" md="1">
                      <b-form-checkbox v-model="item.selected" value="true" class="custom-control-primary">
                      </b-form-checkbox>
                    </b-col>
                    <!-- Item Name -->
                    <b-col md="9">
                      <b-form-textarea id="item-name" type="text" v-model="item.answer" placeholder="Another answer" />
                    </b-col>

                    <!-- Remove Button -->
                    <b-col lg="2" md="2">
                      <b-button v-ripple.400="'rgba(255, 255, 255, 0.15)'" variant="danger" class="btn-icon"
                        @click="removeItem(index)">
                        <feather-icon icon="XIcon" />
                      </b-button>
                    </b-col>
                    <b-col cols="12">
                      <hr />
                    </b-col>
                  </b-row>
                </b-form-group>
              </b-form>

              <b-button v-ripple.400="'rgba(0, 207, 232, 0.15)'" variant="flat-info" @click="repeateAgain">
                <feather-icon icon="PlusIcon" class="mr-25" />
                <span>Add Another Option</span>
              </b-button>
            </div>
          </div>

          <div v-if="
            localQuestion.questionTypeKey != null &&
            localQuestion.questionTypeKey === 'SINGLE_SELECTION'
          ">
            <div>
              <div>
                <b-form ref="form" :style="{ height: trHeight }" class="repeater-form" @submit.prevent="repeateAgain">
                  <b-form-group label="Available Answers" label-for="item-name">
                    <!-- Row Loop -->
                    <b-row v-for="(item, index) in localQuestion.questionAnswers" :id="item.id" :key="item.id"
                      ref="row">
                      <b-col lg="1" md="1">
                        <b-form-radio v-model="localQuestion.acceptedAnswer" :value="item.value"
                          class="demo-inline-spacing" name="radio-validation">
                        </b-form-radio>
                      </b-col>
                      <!-- Item Name -->
                      <b-col md="9">
                        <b-form-textarea id="item-name" type="text" v-model="item.answer"
                          placeholder="Another answer" />
                      </b-col>

                      <!-- Remove Button -->
                      <b-col lg="2" md="2">
                        <b-button v-ripple.400="'rgba(255, 255, 255, 0.15)'" variant="danger" class="btn-icon"
                          @click="removeItem(index)">
                          <feather-icon icon="XIcon" />
                        </b-button>
                      </b-col>
                      <b-col cols="12">
                        <hr />
                      </b-col>
                    </b-row>
                  </b-form-group>
                </b-form>
              </div>
              <b-button v-ripple.400="'rgba(0, 207, 232, 0.15)'" variant="flat-info" @click="repeateAgain">
                <feather-icon icon="PlusIcon" class="mr-25" />
                <span>Add Another Option</span>
              </b-button>
            </div>
          </div>

          <div v-if="
            localQuestion.questionTypeKey != null &&
            localQuestion.questionTypeKey === 'YES_NO_QUESTION'
          ">
            <div>
              <div>
                <b-form ref="form" :style="{ height: trHeight }" class="repeater-form" @submit.prevent="repeateAgain">
                  <b-form-group label="Choose Correct Answers" label-for="item-name">
                    <!-- Row Loop -->
                    <b-row v-for="(item, index) in localQuestion.questionAnswers" :id="item.id" :key="item.id"
                      ref="row">
                      <b-col lg="1" md="1">
                        <b-form-radio v-model="localQuestion.acceptedAnswer" :value="index == 0 ? 'YES' : 'NO'"
                          class="demo-inline-spacing" name="radio-validation">
                        </b-form-radio>
                      </b-col>
                      <b-col md="9">
                        {{ item.answer }}
                      </b-col>

                      <b-col cols="12">
                        <hr />
                      </b-col>
                    </b-row>
                  </b-form-group>
                </b-form>
              </div>
            </div>
          </div>
          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button v-ripple.400="'rgba(255, 255, 255, 0.15)'" variant="primary" class="mr-2" type="submit">
              {{
                (localQuestion != null && localQuestion.id != null) ||
                  localQuestion.id != undefined
                  ? $t("Update")
                  : $t("Add")
              }}
            </b-button>
            <b-button v-ripple.400="'rgba(186, 191, 199, 0.15)'" type="button" variant="outline-secondary"
              @click="hide">
              {{ $t("Cancel") }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BFormCheckbox,
  BFormTextarea,
  BFormRating,
  BRow,
  BCol,
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BButton,
  BFormRadioGroup,
  BFormRadio,
  BFormInvalidFeedback,
  BFormValidFeedback,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { ref, toRefs, watch, inject } from "@vue/composition-api";
import { required, alphaNum, email } from "@validations";
import formValidation from "@core/comp-functions/forms/form-validation";
import Ripple from "vue-ripple-directive";
import vSelect from "vue-select";
import countries from "@/@fake-db/data/other/countries";
import store from "@/store";
import router from "@/router";

import { quillEditor } from "vue-quill-editor";
import SurveyTypePicker from "../components/SurveyQuestionTypePicker.vue";
import QuestionTagPicker from "../components/QuestionTagPicker.vue";
import SurveyThemePicker from "../components/SurveyThemePicker.vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

import { heightTransition } from "@core/mixins/ui/transition";
export default {
  components: {
    BFormCheckbox,
    BFormTextarea,
    BFormRating,
    BRow,
    BCol,
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BButton,
    BFormRadioGroup,
    BFormRadio,
    BFormInvalidFeedback,
    BFormValidFeedback,
    vSelect,
    SurveyTypePicker,
    QuestionTagPicker,
    SurveyThemePicker,
    quillEditor,
    ToastificationContent,
    // Form Validation
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: "isAddNewUserSidebarActive",
    event: "update:is-add-new-user-sidebar-active",
  },
  props: {
    surveyInfo: null,
    isAddNewUserSidebarActive: {
      type: Boolean,
      required: true,
    },
    questionCount: 0,
    roleOptions: {
      type: Array,
      required: true,
    },
    planOptions: {
      type: Array,
      required: true,
    },
    activeQuestion: null,
  },
  mixins: [heightTransition],
  data() {
    return {
      required,
      surveyChecker: 0,
      alphaNum,
      email,
      countries,
      minValue: 1,
      currentId: 0,
      selectedCategory: null,
      questionTag: null,
      questionTypeKey: "",
      questionText: "",
      isUpdated: false,
      maxValue: 10,
      value: null,
      options: [],
      ratingOptions: [
        { value: 1, text: '' },
        { value: 2, text: '' },
        { value: 3, text: '' },
        { value: 4, text: '' },
        { value: 5, text: '' }
      ],
      snowOption: {
        theme: "snow",
        modules: {
          toolbar: [
            ["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ align: [] }],

            [{ color: [] }, { background: [] }],
            ["clean"],
          ],
        },
      },
      localQuestion: {
        required: true,
        title: "",
        examid: "",
        description: "",
        questionType: "",
        questionTypeKey: "",
        allowComment: false,
        acceptedAnswers: [],
        acceptedAnswer: null,
        questionAnswers: [
          {
            id: 1,
            selected: false,
            value: this.generateUUID(),
            answer: null,
          },
        ],
        additionalInfo: {
          min1Value: 1,
          min2Value: 2,
          min3Value: 3,
          min4Value: 4,
          min5Value: 5,

          min1Text: "Poor",
          min2Text: "Needs Improvement",
          min3Text: "Average",
          min4Text: "Good",
          min5Text: "Excellent",

          maxValue: 5,
          minText: "Bad",
          maxText: "Amazing",
        },
      },
    };
  },
  mounted() {
    this.initTrHeight();
  },
  created() {
    //console.log('created');
    this.questionTag =
      this.activeQuestion == null ? null : this.activeQuestion.questionTag;
    //console.log(this.questionTag);
    window.addEventListener("resize", this.initTrHeight);
  },
  destroyed() {
    window.removeEventListener("resize", this.initTrHeight);
  },
  computed: {
    state() {
      return Boolean(this.value);
    },
    answers() {
      //console.log("---");
      //console.log(this.localQuestion.questionAnswers);

      return JSON.parse(JSON.stringify(this.localQuestion.questionAnswers));
    },
  },
  watch: {
    activeQuestion(newVal, oldVal) {
      if (newVal) {
        this.localQuestion = newVal;
        //console.log("incoming new wall");
        //console.log(this.localQuestion);

        if (
          newVal != null &&
          newVal.additionalInfo != null &&
          newVal != undefined &&
          newVal.additionalInfo != undefined
        ) {
          this.additionalInfo = newVal.additionalInfo;
          this.questionTag = newVal.questionTag;
        } else {
          this.questionTag = newVal.questionTag;

          this.additionalInfo = {
            min1Value: 1,
            min2Value: 2,
            min3Value: 3,
            min4Value: 4,
            min5Value: 5,
            min1Text: "Poor",
            min2Text: "Needs Improvement",
            min3Text: "Average",
            min4Text: "Good",
            min5Text: "Excellent",
            maxValue: 5,
            minText: "Bad",
            maxText: "Amazing",
          };
        }

        //console.log("question updated");
        //console.log(this.localQuestion);
        this.selectedCategory = this.localQuestion.questionTypeKey;
        // Set questionText from description
        if (newVal.description) {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = newVal.description;
          this.questionText = (tempDiv.textContent || tempDiv.innerText || '').trim();
        } else {
          this.questionText = '';
        }
        
        // Load rating options from additionalInfo
        if (newVal.additionalInfo) {
          this.loadRatingOptionsFromAdditionalInfo(newVal.additionalInfo);
        }
      }
      this.currentId++;
    },
    ratingOptions: {
      handler(newVal) {
        // Auto-sync rating options to additionalInfo when changed
        this.syncRatingOptionsToAdditionalInfo();
      },
      deep: true
    }
  },
  methods: {
    getValidationState({ dirty, validated, valid = null }) {
      return dirty || validated ? valid : null
    },
    
    // Rating options methods
    addRatingOption() {
      if (this.ratingOptions.length < 10) {
        const newValue = this.ratingOptions.length + 1;
        this.ratingOptions.push({ value: newValue, text: '' });
        this.syncRatingOptionsToAdditionalInfo();
      }
    },
    
    removeRatingOption(index) {
      if (this.ratingOptions.length > 2) {
        this.ratingOptions.splice(index, 1);
        // Re-index values
        this.ratingOptions.forEach((option, idx) => {
          option.value = idx + 1;
        });
        this.syncRatingOptionsToAdditionalInfo();
      }
    },
    
    getDefaultPlaceholder(value) {
      const placeholders = {
        1: 'Poor',
        2: 'Needs Improvement',
        3: 'Average',
        4: 'Good',
        5: 'Excellent'
      };
      return placeholders[value] || `Option ${value}`;
    },
    
    syncRatingOptionsToAdditionalInfo() {
      // Convert ratingOptions array to additionalInfo object format
      const info = {};
      
      // Add min values and texts
      this.ratingOptions.forEach((option, index) => {
        const key = index + 1;
        info[`min${key}Value`] = option.value;
        info[`min${key}Text`] = option.text;
      });
      
      // Add additional fields
      info.maxValue = this.ratingOptions.length;
      info.minText = this.ratingOptions[0]?.text || "Bad";
      info.maxText = this.ratingOptions[this.ratingOptions.length - 1]?.text || "Amazing";
      
      this.localQuestion.additionalInfo = info;
    },
    
    loadRatingOptionsFromAdditionalInfo(additionalInfo) {
      // Convert additionalInfo object to ratingOptions array
      const options = [];
      let index = 1;
      while (additionalInfo[`min${index}Value`] !== undefined) {
        options.push({
          value: additionalInfo[`min${index}Value`] || index,
          text: additionalInfo[`min${index}Text`] || ''
        });
        index++;
      }
      // If no options found, initialize with default 5 options
      if (options.length === 0) {
        options.push(
          { value: 1, text: '' },
          { value: 2, text: '' },
          { value: 3, text: '' },
          { value: 4, text: '' },
          { value: 5, text: '' }
        );
      }
      this.ratingOptions = options;
    },
    resetForm() {
      this.questionText = '';
      this.questionTag = null;
      // Reset rating options to default 5 options
      this.ratingOptions = [
        { value: 1, text: '' },
        { value: 2, text: '' },
        { value: 3, text: '' },
        { value: 4, text: '' },
        { value: 5, text: '' }
      ];
    },
    generateUUID() {
      // Public Domain/MIT
      var d = new Date().getTime(); //Timestamp
      var d2 =
        (typeof performance !== "undefined" &&
          performance.now &&
          performance.now() * 1000) ||
        0; //Time in microseconds since page-load or 0 if unsupported
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (
        c
      ) {
        var r = Math.random() * 16; //random number between 0 and 16
        if (d > 0) {
          //Use timestamp until depleted
          r = (d + r) % 16 | 0;
          d = Math.floor(d / 16);
        } else {
          //Use microseconds since page-load if supported
          r = (d2 + r) % 16 | 0;
          d2 = Math.floor(d2 / 16);
        }
        return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
      });
    },
    repeateAgain() {
      this.localQuestion.questionAnswers.push({
        id: 1,
        selected: false,
        value: this.generateUUID(),
        answer: "",
      });

      this.$nextTick(() => {
        this.trAddHeight(this.$refs.row[0].offsetHeight);
      });
    },
    removeItem(index) {
      var items = this.localQuestion.questionAnswers;

      //        var result = items.filter(obj => {
      //   return obj.value=== item
      // })

      items.splice(index, 1);
      this.trTrimHeight(this.$refs.row[0].offsetHeight);
    },
    initTrHeight() {
      this.trSetHeight(null);
      this.$nextTick(() => {
        // this.trSetHeight(this.$refs.form.scrollHeight)
      });
    },
    handleQuestionSubmit() {
      //console.log("Survey ADD Pquestion  Create triggered");
      
      // Sync rating options to additionalInfo before submitting
      this.syncRatingOptionsToAdditionalInfo();
      
      var surveyid = store.getters["app/currentUnit"];
      this.localQuestion.surveyid = surveyid;

      this.localQuestion.questionOrder = this.questionCount + 1;
      //console.log(this.localQuestion);

      // Set question type to Range Selection for all surveys
      this.localQuestion.questionType = "Range Question";
      this.localQuestion.questionTypeKey = "RANGE_SELECTION";
      this.selectedCategory = {
        questionType: "Range Question",
        questionTypeKey: "RANGE_SELECTION",
      };

      store
        .dispatch("app-survey-questions/addSurveyQuestion", this.localQuestion)
        .then(() => {
          this.selectedCategory = null;
          this.userData = {
            title: "",
            surveyid: "",
            description: "",
            questionType: "",
            questionTypeKey: "",
            allowComment: false,
            acceptedAnswers: [],
            acceptedAnswer: null,
            questionAnswers: [
              {
                id: 1,
                selected: false,
                value: this.generateUUID(),
                answer: null,
              },
            ],
            additionalInfo: {
              min1Value: 1,
              min2Value: 2,
              min3Value: 3,
              min4Value: 4,
              min5Value: 5,

              min1Text: "Poor",
              min2Text: "Needs Improvement",
              min3Text: "Average",
              min4Text: "Good",
              min5Text: "Excellent",

              maxValue: 5,
              minText: "Bad",
              maxText: "Amazing",
            },
          };

          this.additionalInfo = {
            min1Value: 1,
            min2Value: 2,
            min3Value: 3,
            min4Value: 4,
            min5Value: 5,

            min1Text: "Poor",
            min2Text: "Needs Improvement",
            min3Text: "Average",
            min4Text: "Good",
            min5Text: "Excellent",

            maxValue: 5,
            minText: "Bad",
            maxText: "Amazing",
          };

          this.$emit("refetch-data");
          this.$emit("update:is-add-new-user-sidebar-active", false);
        })
        .catch((error) => {
          console.error("Error adding survey question:", error);
          
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('survey.surveyattendee.error'),
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: this.$t(`${error.response?.data?.message}`) || this.$t(`${error.message}`) || this.$t('survey.error_adding_question')
            },
          });
        });
    },

    QuestionTagUpdated(payload) {
      //console.log("question result in info ");
      //console.log(payload);
      if (payload == null || payload == "" || payload == undefined) {
        this.questionTag = null;
        this.questionTagType = null;
        (this.localQuestion.questionTag = null),
          (this.localQuestion.questionTagKey = null);
      } else {
        //console.log("question type  data updated ");
        //console.log(payload.typekey);

        this.questionTagType = payload.typekey;
        this.questionTag = payload.title;
        this.questionTag = payload;

        (this.localQuestion.questionTag = payload.title),
          (this.localQuestion.questionTagType = payload.typekey);
      }
    },

    SurveyTypeUpdated(payload) {
      //console.log("question result in info ");
      //console.log(payload);
      if (payload == null) {
        this.questionTypeKey = null;
        this.questionType = null;
        (this.localQuestion.questionType = null),
          (this.localQuestion.questionTypeKey = null);
      } else {
        //console.log("question type  data updated "+payload.typekey);

        this.questionTypeKey = payload.typekey;
        this.questionType = payload.title;
        this.selectedCategory = payload;

        (this.localQuestion.questionType = payload.title),
          (this.localQuestion.questionTypeKey = payload.typekey);

        if (payload.typekey == "YES_NO_QUESTION") {
          this.localQuestion.questionAnswers = [
            {
              id: 1,
              selected: false,
              value: "YES",
              answer: "YES",
            },
            {
              id: 2,
              selected: false,
              value: "NO",
              answer: "NO",
            },
          ];
        }
      }
    },
    onEditorBlur(quill) {
      //    //console.log('editor blur!', quill)
    },
    onEditorFocus(quill) {
      //   //console.log('editor focus!', quill)
    },
    onEditorReady(quill) {
      //     //console.log('editor ready!', quill)
    },
    onEditorChange({ quill, html, text }) {
      //     //console.log('editor change!', quill, html, text)
      this.isUpdated = true;
      this.localQuestion.description = html;
      this.questionText = text.trim();
    },
  },
  setup(props, { emit }) {
    return;
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

#add-new-user-sidebar {
  .vs__dropdown-menu {
    max-height: 200px !important;
  }
}
</style>
<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";

@import "@core/scss/vue/libs/quill.scss";

.ql-editor {
  min-height: 100px;
}

.is-invalid .quill-editor .ql-container {
  border-color: #ea5455 !important;
}

.is-invalid .quill-editor .ql-toolbar {
  border-color: #ea5455 !important;
}

.is-valid .quill-editor .ql-container {
  border-color: #28c76f !important;
}

.is-valid .quill-editor .ql-toolbar {
  border-color: #28c76f !important;
}

// Question Tag Picker validation styles
.is-invalid .v-select .vs__dropdown-toggle {
  border-color: #ea5455 !important;
}

.is-valid .v-select .vs__dropdown-toggle {
  border-color: #28c76f !important;
}
</style>
